import React from 'react'
import { observer } from 'mobx-react'

import BookkeepingManagerViewModel from 'admin/bookkeeping/BookkeepingManagerViewModel'
import BookkeepingManagerHeader from 'admin/bookkeeping/components/BookkeepingManagerHeader'
import {
  SideSheet,
  SideSheetHostView,
  SideSheetMain,
} from 'components/SideSheet'
import { useDocumentTitle } from 'hooks/document-title'
import { useActivation } from 'hooks/mobx-hooks'
import { useScrollOffsetSpring } from 'hooks/spring-hooks'
import BookkeepingManagerActionFilter from 'admin/bookkeeping/components/BookkeepingManagerActionFilter'
import ContentLoading from 'components/ContentLoading'
import ContentError from 'components/ContentError'
import { ContentLayout } from 'components/ContentLayout'
import { TableHost, TableScrollingContainer } from 'components/Table'
import { TableSearchbar } from 'payouts/components/TableSearchbar'
import BookkeepingManagerTable from 'admin/bookkeeping/components/BookkeepingManagerTable'
import BookkeepingManagerTableTotal from 'admin/bookkeeping/components/BookkeepingManagerTableTotal'
import { Sandwich, SandwichBun } from 'components/Sandwich'
import BookkeepingManagerDetails from 'admin/bookkeeping/components/BookkeepingManagerDetails'

import styles from './BookkeepingManager.sass'

interface BookkeepingManagerProps {
  vm: BookkeepingManagerViewModel
}

export default observer(function BookkeepingManager({
  vm,
}: BookkeepingManagerProps) {
  useDocumentTitle('Bookkeeping Manager')
  useActivation(vm)

  return vm.activate.match({
    pending: () => <ContentLoading />,
    rejected: (err) => <ContentError error={err} />,
    resolved: () => <BookkeepingManagerContent vm={vm} />,
  })
})

const BookkeepingManagerContent = observer(
  function BookkeepingManagerContentImpl({ vm }: BookkeepingManagerProps) {
    const scrollRef = React.useRef<HTMLDivElement | null>(null)
    const scrollOffset = useScrollOffsetSpring(scrollRef)

    return (
      <div className={styles.root} ref={scrollRef}>
        <BookkeepingManagerHeader scrollOffset={scrollOffset} vm={vm} />
        <SideSheetHostView sheetVisible={vm.detailsVM.showing} ref={scrollRef}>
          <SideSheetMain>
            <Sandwich stretch>
              <SandwichBun>
                <BookkeepingManagerActionFilter
                  filter={vm.searcher.actionFilter}
                />
              </SandwichBun>
              <BookkeepingManagerTableTotal vm={vm} />
              <ContentLayout>
                <TableHost>
                  <TableSearchbar
                    searchText={vm.searcher.searchText.value}
                    onSearchTextChange={vm.searcher.searchText.change}
                  />
                  <TableScrollingContainer>
                    <BookkeepingManagerTable vm={vm} />
                  </TableScrollingContainer>
                </TableHost>
              </ContentLayout>
            </Sandwich>
          </SideSheetMain>
          <SideSheet>
            <BookkeepingManagerDetails vm={vm.detailsVM} />
          </SideSheet>
        </SideSheetHostView>
      </div>
    )
  },
)
